import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ChevronRight, ChevronDown, LayoutGrid } from "lucide-react";
import { Input } from "@/components/ui/input";

export const CategorySidebar = ({ categories, activeCat, activeSub, onNavigate }) => {
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(activeCat ? { [activeCat]: true } : {});

  const filtered = useMemo(() => {
    if (!q.trim()) return categories;
    const t = q.toLowerCase();
    return categories
      .map((c) => {
        const subMatch = (c.subcategories || []).filter((s) => s.name.toLowerCase().includes(t));
        if (c.name.toLowerCase().includes(t)) return c;
        if (subMatch.length) return { ...c, subcategories: subMatch, _forceOpen: true };
        return null;
      })
      .filter(Boolean);
  }, [categories, q]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white" data-testid="category-sidebar">
      <div className="p-3 border-b border-slate-100 sticky top-0 bg-white rounded-t-2xl z-10">
        <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1"><LayoutGrid className="h-3.5 w-3.5" /> Categories</p>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search categories…" className="pl-8 h-9 text-sm" data-testid="sidebar-search" />
        </div>
      </div>
      <nav className="p-2 space-y-0.5">
        <Link to="/vendors" onClick={onNavigate} className={`block px-2.5 py-1.5 rounded-lg text-sm font-medium ${!activeCat ? "bg-teal-brand/10 text-teal" : "text-slate-600 hover:bg-slate-50"}`} data-testid="sidebar-all">All Categories</Link>
        {filtered.map((c) => {
          const open = expanded[c.slug] || c._forceOpen;
          const isActive = activeCat === c.slug;
          return (
            <div key={c.slug}>
              <div className={`flex items-center rounded-lg ${isActive && !activeSub ? "bg-teal-brand/10" : "hover:bg-slate-50"}`}>
                <Link to={`/vendors/${c.slug}`} onClick={onNavigate} data-testid={`sidebar-cat-${c.slug}`}
                  className={`flex-1 px-2.5 py-1.5 text-sm truncate ${isActive ? "text-teal font-semibold" : "text-slate-700"}`}>{c.name}</Link>
                {c.subcategories?.length > 0 && (
                  <button onClick={() => setExpanded((p) => ({ ...p, [c.slug]: !open }))} className="p-1.5 text-slate-400" data-testid={`sidebar-toggle-${c.slug}`}>
                    {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                )}
              </div>
              {open && c.subcategories?.length > 0 && (
                <div className="ml-3 pl-2 border-l border-slate-100 py-0.5 space-y-0.5">
                  {c.subcategories.map((s) => (
                    <Link key={s.slug} to={`/vendors/${c.slug}/${s.slug}`} onClick={onNavigate} data-testid={`sidebar-sub-${s.slug}`}
                      className={`block px-2 py-1 rounded text-[13px] ${activeSub === s.slug ? "text-teal font-semibold bg-teal-brand/5" : "text-slate-500 hover:text-teal"}`}>{s.name}</Link>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {!filtered.length && <p className="px-2.5 py-3 text-xs text-slate-400">No categories match “{q}”.</p>}
      </nav>
    </div>
  );
};
